import { DragEndEvent, DragStartEvent } from '@dnd-kit/core';
import {
  deleteV1CompaniesCompanyIdJobsId,
  getV1CompaniesCompanyIdEquipmentTypeahead,
  GetV1CompaniesCompanyIdEquipmentTypeaheadData,
  getV1CompaniesCompanyIdJobs,
  getV1CompaniesCompanyIdJobsBulkSendDay,
  GetV1CompaniesCompanyIdJobsData,
  getV1CompaniesCompanyIdJobsJobIdEquipmentTypeahead,
  GetV1CompaniesCompanyIdJobsJobIdEquipmentTypeaheadData,
  getV1CompaniesCompanyIdOrdersDispatch,
  GetV1CompaniesCompanyIdOrdersDispatchData,
  getV1CompaniesCompanyIdOrdersDispatchNumbersTypeahead,
  GetV1CompaniesCompanyIdOrdersDispatchNumbersTypeaheadData,
  getV1CompaniesCompanyIdOrdersOrderIdJobs,
  GetV1CompaniesCompanyIdOrdersOrderIdJobsData,
  Job_Read,
  Job_Update,
  JobStateEvent,
  ModelError_Response,
  Order_Copy,
  Order_Create,
  Order_Read,
  Order_Update,
  patchV1CompaniesCompanyIdJobsId,
  patchV1CompaniesCompanyIdOrdersId,
  postV1CompaniesCompanyIdJobsJobIdCopy,
  postV1CompaniesCompanyIdOrders,
  postV1CompaniesCompanyIdOrdersOrderIdCopy,
  postV1CompaniesCompanyIdOrdersOrderIdJobs,
  putV1CompaniesCompanyIdJobsBulkAssign,
  PutV1CompaniesCompanyIdJobsBulkAssignData,
  putV1CompaniesCompanyIdJobsBulkCancel,
  putV1CompaniesCompanyIdJobsBulkCopyAssignments,
  PutV1CompaniesCompanyIdJobsBulkCopyAssignmentsData,
  putV1CompaniesCompanyIdJobsBulkSend,
  putV1CompaniesCompanyIdJobsBulkSendDay,
  putV1CompaniesCompanyIdJobsBulkUnassign,
  putV1CompaniesCompanyIdJobsJobIdAccept,
  putV1CompaniesCompanyIdJobsJobIdCancel,
  putV1CompaniesCompanyIdJobsJobIdReject,
  putV1CompaniesCompanyIdJobsJobIdRequest,
  putV1CompaniesCompanyIdOrdersOrderIdAccept,
  putV1CompaniesCompanyIdOrdersOrderIdCancel,
  putV1CompaniesCompanyIdOrdersOrderIdComplete,
  putV1CompaniesCompanyIdOrdersOrderIdReject,
  PutV1CompaniesCompanyIdOrdersOrderIdRejectData,
  UnitOfDistance,
  UserBulkAssignJobsChannel_Read,
  UserBulkCopyAssignmentsChannel_Read,
} from '@treadinc/horizon-api-spec';
import axios, { AxiosError, isAxiosError } from 'axios';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { isUndefined } from 'lodash-es';
import { useCallback, useContext, useState } from 'react';

import { OrderOption } from '~components/OrderBySelector/OrderBySelector';
import BulkSendProgressContext from '~contexts/BulkSendProgressContext';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import useCancelToken from '~hooks/useCancelToken/useCancelToken';
import useComplianceCheck, {
  ComplianceCheckResult,
} from '~hooks/useComplianceCheck/useComplianceCheck';
import { EquipmentTypeahead } from '~hooks/useEquipment/models';
import { Job } from '~hooks/useJob/models';
import { parseAssigneeDragId } from '~pages/Dispatch/components/assigneeDragId';
import { NewOrderFormSchemaInterface } from '~pages/Sales/Orders/newOrderFormSchema';
import connection from '~services/connectionModule';
import { extractPagination } from '~services/pagination';
import { realTimeChannels } from '~services/realTimeChannels';
import { useStores } from '~store';
import { Filters, OrdersSort } from '~store/OrdersDispatchStore';
import { alert, AlertTypes } from '~types/AlertTypes';
import { fetchAllApiPages } from '~utils/fetchAllPages';
import {
  subscribeWithMultipleHandlers,
  unsubscribeHandler,
} from '~utils/rtu/rtu-multi-handler';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import { DriverTypeahead } from '../useDrivers/models';
import { useJob } from '../useJob/useJob';
import buildDispatchSearchRequest, {
  toDispatchOrdersQueryParams,
} from './buildDispatchSearchRequest';
import buildOrderJobsFilter, { toOrderJobsQueryParams } from './buildOrderJobsFilter';
import { Order } from './models';

type OrderJobsQuery = NonNullable<GetV1CompaniesCompanyIdOrdersOrderIdJobsData['query']>;

type GetCompanyEquipmentsTypeaheadQueryParams = NonNullable<
  GetV1CompaniesCompanyIdEquipmentTypeaheadData['query']
>;

type GetJobEquipmentsTypeaheadQueryParams = NonNullable<
  GetV1CompaniesCompanyIdJobsJobIdEquipmentTypeaheadData['query']
>;

type CompanyJobsQuery = NonNullable<GetV1CompaniesCompanyIdJobsData['query']>;

interface GetDispatchNumbersByCompanyTypeaheadProps {
  companyId: string;
  limit?: number;
  query?: string;
  cursor?: string;
}

type CloneOrderV2Params = {
  date?: string;
  includeAssignee?: boolean;
  orderId: string;
  truckCount?: number;
};

interface PendingDragAssignment {
  jobId: string;
  assigneeId: string;
  orderId: string;
  isDriver: boolean;
  equipmentTypeId?: string;
  complianceResult: ComplianceCheckResult;
}

interface PendingTypeMismatchAssignment {
  jobId: string;
  assigneeId: string;
  orderId: string;
  equipmentTypeId: string;
  equipmentTypeName: string;
  requestedEquipmentTypeName: string;
  vendorName: string;
}

const ORDERS_DISPATCH_JOBS_RTU_HANDLER_KEY =
  'ordersDispatchStore:CompanyJobUpdateChannel';

// Builds the dispatch-orders query from the store filters + sort, minus pagination (the caller
// owns page[limit]/before/after).
const buildDispatchOrdersBaseQuery = (
  filters: Filters,
  ordersSort: OrderOption<OrdersSort> | undefined,
  { includeDates }: { includeDates: boolean },
) => {
  const query: NonNullable<GetV1CompaniesCompanyIdOrdersDispatchData['query']> =
    toDispatchOrdersQueryParams(buildDispatchSearchRequest(filters, { includeDates }));

  if (ordersSort) {
    const { orderBy, sortBy } = ordersSort;

    query['sort[account][name]'] =
      orderBy === OrdersSort.ACCOUNT_NAME ? sortBy : undefined;
    query['sort[company][legal_name]'] =
      orderBy === OrdersSort.COMPANY_LEGAL_NAME ? sortBy : undefined;
    query['sort[created_at]'] = orderBy === OrdersSort.CREATED_AT ? sortBy : undefined;
    query['sort[dispatch_number][name]'] =
      orderBy === OrdersSort.DISPATCH_NUMBER_NAME ? sortBy : undefined;
    query['sort[friendly_order_id]'] =
      orderBy === OrdersSort.FRIENDLY_ORDER_ID ? sortBy : undefined;
    query['sort[load_at]'] = orderBy === OrdersSort.LOAD_AT ? sortBy : undefined;
    query['sort[order_summary][jobs_count]'] =
      orderBy === OrdersSort.TRUCK_COUNT ? sortBy : undefined;
  }

  return query;
};

export function isRequestedEquipmentTypeMismatch(
  requestedEquipmentType: { id?: string | null; name?: string | null } | null | undefined,
  draggedEquipmentTypeId: string,
  draggedEquipmentTypeName?: string,
) {
  if (!requestedEquipmentType) {
    return false;
  }

  if (requestedEquipmentType.id) {
    return requestedEquipmentType.id !== draggedEquipmentTypeId;
  }

  if (!requestedEquipmentType.name || !draggedEquipmentTypeName) {
    return false;
  }

  return (
    requestedEquipmentType.name.trim().toLowerCase() !==
    draggedEquipmentTypeName.trim().toLowerCase()
  );
}

function resolveRequestedEquipmentType(job: Job | undefined, order: Order | undefined) {
  if (job?.requestedEquipmentTypeName) {
    return { id: null, name: job.requestedEquipmentTypeName };
  }

  return order?.equipmentType;
}

export default function useOrdersDispatch() {
  const { ordersDispatchStore, ordersAssignedStore, toasterStore, userStore } =
    useStores();
  const { enqueueBulkSend } = useContext(BulkSendProgressContext);
  const { assignDriver, assignVendor, doEvent, unassignDriver } = useJob();
  const { check: checkCompliance } = useComplianceCheck();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [pendingDragAssignment, setPendingDragAssignment] =
    useState<PendingDragAssignment | null>(null);
  const [pendingTypeMismatchAssignment, setPendingTypeMismatchAssignment] =
    useState<PendingTypeMismatchAssignment | null>(null);
  const [isDragOverrideSubmitting, setIsDragOverrideSubmitting] = useState(false);
  const [isTypeMismatchSubmitting, setIsTypeMismatchSubmitting] = useState(false);
  const shouldUseKilometers = userStore?.userCompany?.isMeters;

  const acceptJob = async (jobId: string, companyId: string) => {
    ordersDispatchStore.orderJobAcceptStart(jobId);

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdAccept({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: {},
      });

      const acceptedJob = Job.parse(response.data.data);
      ordersDispatchStore.orderJobAcceptEnd(jobId, acceptedJob);

      return acceptedJob;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_accept'));
      throw error;
    } finally {
      ordersDispatchStore.orderJobAcceptEnd(jobId);
    }
  };

  const acceptOrder = useCallback(
    async (orderId: string) => {
      ordersDispatchStore.orderAcceptStart(orderId);

      try {
        const response = await putV1CompaniesCompanyIdOrdersOrderIdAccept({
          path: { 'company-id': companyId, 'order-id': orderId },
          body: {},
        });

        const acceptedOrder = Order.parse(response.data.data);
        ordersDispatchStore.orderAcceptEnd(acceptedOrder);

        return acceptedOrder;
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.orders.failed_to_accept'));
        throw error;
      } finally {
        ordersDispatchStore.orderAcceptEnd();
      }
    },
    [companyId, ordersDispatchStore],
  );

  const bulkAssignJobs = async (
    jobIds: string[],
    vendorAccountId: string,
    companyId: string,
    complianceOverride?: boolean,
  ) => {
    try {
      const body: PutV1CompaniesCompanyIdJobsBulkAssignData['body'] & {
        compliance_override?: boolean;
      } = {
        job_ids: jobIds,
        vendor_account_id: vendorAccountId,
        ...(complianceOverride ? { compliance_override: true } : {}),
      };

      await putV1CompaniesCompanyIdJobsBulkAssign({
        path: { 'company-id': companyId },
        body,
      });
      AnalyticsController.track('job_assignment_submitted', {
        method: 'bulk_vendor',
        assignee_type: 'vendor',
        job_count: jobIds.length,
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_assign_jobs'),
      );
    }
  };

  const bulkCopyAssignments = async (
    jobIds: string[],
    orderId: string,
    companyId: string,
    deepCopy?: boolean,
  ) => {
    try {
      const body: PutV1CompaniesCompanyIdJobsBulkCopyAssignmentsData['body'] = {
        deep_copy: deepCopy,
        job_ids: jobIds,
        order_id: orderId,
      };

      await putV1CompaniesCompanyIdJobsBulkCopyAssignments({
        path: { 'company-id': companyId },
        body,
      });
      AnalyticsController.track('job_assignment_submitted', {
        method: 'copy_assignments',
        job_count: jobIds.length,
      });
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_copy_assignments'),
      );
    }
  };

  const bulkSendJobs = async (jobIds: string[], companyId: string) => {
    try {
      const response = await putV1CompaniesCompanyIdJobsBulkSend({
        path: { 'company-id': companyId },
        body: { job_ids: jobIds },
      });
      const { id: sendId, job_ids: sentJobIds } = response.data.data;
      enqueueBulkSend(sendId, sentJobIds);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_send_jobs'),
      );
    }
  };

  const bulkUnassignJobs = async (jobIds: string[], companyId: string) => {
    try {
      await putV1CompaniesCompanyIdJobsBulkUnassign({
        path: { 'company-id': companyId },
        body: { job_ids: jobIds },
      });
      ordersDispatchStore.setJobsWaitingForRTU(jobIds);
      toasterStore.push(
        alert(t('dispatch.job.bulk_unassign_jobs_success'), AlertTypes.success),
      );
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_unassign_jobs'),
      );
    }
  };

  const bulkCancelJobs = async (jobIds: string[], companyId: string) => {
    try {
      await putV1CompaniesCompanyIdJobsBulkCancel({
        path: { 'company-id': companyId },
        body: { job_ids: jobIds },
      });
      ordersDispatchStore.setJobsWaitingForRTU(jobIds);
      toasterStore.push(
        alert(t('dispatch.job.bulk_cancel_jobs_success'), AlertTypes.success),
      );
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_cancel_jobs'),
      );
    }
  };

  const getBulkSendJobsByDayCount = useCallback(
    async (date: string, filters: Filters, companyId: string) => {
      try {
        const response = await getV1CompaniesCompanyIdJobsBulkSendDay({
          path: { 'company-id': companyId },
          query: {
            ...buildDispatchOrdersBaseQuery(filters, undefined, { includeDates: false }),
            date,
          },
        });

        return response.data.data.count;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_fetch_bulk_send_jobs_by_day_count'),
        );
        throw error;
      }
    },
    [],
  );

  const bulkSendJobsByDay = async (date: string, filters: Filters, companyId: string) => {
    try {
      const response = await putV1CompaniesCompanyIdJobsBulkSendDay({
        path: { 'company-id': companyId },
        body: { ...buildDispatchSearchRequest(filters, { includeDates: false }), date },
      });
      const { id: sendId, job_ids: jobIds } = response.data.data;
      enqueueBulkSend(sendId, jobIds);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_bulk_send_jobs_by_day'),
      );
      // Unlike the other bulk actions, rethrown so the Send All dialog stays open to retry.
      throw error;
    }
  };

  const cancelJob = async (jobId: string, companyId: string) => {
    ordersDispatchStore.orderJobCancelStart(jobId);

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdCancel({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: {},
      });

      const cancelledJob = Job.parse(response.data.data);
      ordersDispatchStore.orderJobCancelEnd(cancelledJob);

      return cancelledJob;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_cancel'));
      throw error;
    } finally {
      ordersDispatchStore.orderJobCancelEnd();
    }
  };

  const cancelOrder = useCallback(
    async (orderId: string) => {
      ordersDispatchStore.orderCancelStart();

      try {
        const response = await putV1CompaniesCompanyIdOrdersOrderIdCancel({
          path: { 'company-id': companyId, 'order-id': orderId },
          body: {},
        });

        const cancelledOrder = Order.parse(response.data.data);
        ordersDispatchStore.orderCancelEnd(cancelledOrder);

        return cancelledOrder;
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.orders.failed_to_cancel'));
        throw error;
      } finally {
        ordersDispatchStore.orderCancelEnd();
      }
    },
    [companyId, ordersDispatchStore],
  );

  const cloneOrderV2 = async (params: CloneOrderV2Params) => {
    try {
      const body: Order_Copy = {
        date: params.date,
        include_assignee: params.includeAssignee,
        truck_count: params.truckCount,
      };

      const response = await postV1CompaniesCompanyIdOrdersOrderIdCopy({
        path: {
          'company-id': companyId,
          'order-id': params.orderId,
        },
        body,
      });

      return Order.parse(response.data.data);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_duplicate'),
      );
      throw error;
    }
  };

  const completeOrder = useCallback(
    async (orderId: string) => {
      ordersDispatchStore.orderCompleteStart();

      try {
        const response = await putV1CompaniesCompanyIdOrdersOrderIdComplete({
          path: { 'company-id': companyId, 'order-id': orderId },
          body: {},
        });

        const completedOrder = Order.parse(response.data.data);
        ordersDispatchStore.orderCompleteEnd(completedOrder);

        return completedOrder;
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.orders.failed_to_complete'),
        );
        throw error;
      } finally {
        ordersDispatchStore.orderCompleteEnd();
      }
    },
    [companyId, ordersDispatchStore],
  );

  const createJobFromOrder = useCallback(
    async (orderId: string) => {
      ordersDispatchStore.jobCreateFromOrderStart(orderId);

      try {
        const response = await postV1CompaniesCompanyIdOrdersOrderIdJobs({
          path: { 'company-id': companyId, 'order-id': orderId },
        });

        const createdJob = Job.parse(response.data.data);
        ordersDispatchStore.jobCreateFromOrderEnd(orderId, createdJob);

        return createdJob;
      } catch (error) {
        connection.handleRequestError(error, t('error_messages.jobs.failed_to_create'));
        throw error;
      } finally {
        ordersDispatchStore.jobCreateFromOrderEnd(orderId);
      }
    },
    [companyId, ordersDispatchStore],
  );

  const createOrder = async (order: NewOrderFormSchemaInterface) => {
    ordersDispatchStore.orderCreateStart();

    try {
      const distanceUnit = shouldUseKilometers
        ? UnitOfDistance.KILOMETER
        : UnitOfDistance.MILE;
      const response = await postV1CompaniesCompanyIdOrders({
        path: {
          'company-id': order.company?.id ?? companyId,
        },
        body: Order.deparse(order, distanceUnit),
      });

      const createdOrder = Order.parse(response.data.data);
      ordersDispatchStore.orderCreateEnd(createdOrder);

      return createdOrder;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_create'));
      throw error;
    } finally {
      ordersDispatchStore.orderCreateEnd();
    }
  };

  // order-form-v3: the V3 form deparses to a fully-typed
  // Order_Create / Partial<Order_Update> body via deparseCreateV3 /
  // deparseUpdateV3 in OrderForm.tsx. These helpers accept that pre-built body
  // directly, mirroring useProjects' createProjectV2 / updateProjectV2. The
  // legacy createOrder / updateOrder above still go through Order.deparse /
  // Order.deparseUpdate so the flag-off path stays byte-identical. A 422
  // rethrows WITHOUT the generic toast — the caller maps ModelError_Item[] to
  // inline field errors (the same pattern ProjectDialog uses); anything else
  // keeps the legacy toast-and-rethrow.
  const createOrderV3 = async ({ body }: { body: Order_Create }) => {
    ordersDispatchStore.orderCreateStart();

    try {
      const response = await postV1CompaniesCompanyIdOrders({
        path: {
          // Same source as legacy: the payload's company wins, the user's
          // effective company is the fallback (createOrder above).
          'company-id': body.company_id ?? companyId,
        },
        body,
      });

      const createdOrder = Order.parse(response.data.data);
      ordersDispatchStore.orderCreateEnd(createdOrder);

      return createdOrder;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.orders.failed_to_create'));
      throw error;
    } finally {
      ordersDispatchStore.orderCreateEnd();
    }
  };

  const duplicateJob = async (jobId: string) => {
    ordersDispatchStore.orderJobDuplicateStart(jobId);

    try {
      const response = await postV1CompaniesCompanyIdJobsJobIdCopy({
        path: {
          'company-id': getEffectiveUserCompanyId(userStore),
          'job-id': jobId,
        },
        body: {},
      });

      const job = Job.parse(response.data.data);
      ordersDispatchStore.orderJobDuplicateEnd(job);

      return job;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_duplicate'));
      throw error;
    } finally {
      ordersDispatchStore.orderJobDuplicateEnd();
    }
  };

  const deleteJob = async (jobId: string) => {
    try {
      ordersDispatchStore.orderJobDeleteStart(jobId);

      await deleteV1CompaniesCompanyIdJobsId({
        path: { 'company-id': getEffectiveUserCompanyId(userStore), id: jobId },
      });
      ordersDispatchStore.orderJobDeleteEnd(jobId);
      return jobId;
    } catch (error) {
      ordersDispatchStore.orderJobDeleteEnd(jobId, true);

      if (isAxiosError(error) && error.response?.status === 422) {
        const e = error as AxiosError<ModelError_Response>;
        const message = e.response?.data.error.errors?.[0]?.message;

        if (message?.includes('cannot delete the last Job on an Order')) {
          toasterStore.push(
            alert(
              t('dispatch.dispatch_v2.cannot_delete_last_job_on_order'),
              AlertTypes.info,
            ),
          );

          return;
        }
      }

      connection.handleRequestError(error, t('error_messages.jobs.failed_to_delete'));
    }
  };

  const getCompanyEquipmentsTypeahead = async (
    companyId: string,
    query: string,
    pagination: { after?: string; limit: number },
    shared?: boolean,
  ) => {
    const params: GetCompanyEquipmentsTypeaheadQueryParams = {
      'page[limit]': pagination.limit,
      'filter[dispatchable]': true,
      'search[query]': query || undefined,
    };

    if (pagination.after) {
      params['page[after]'] = pagination.after;
    }

    if (!isUndefined(shared)) {
      params['filter[shared]'] = shared;
    }

    try {
      const response = await getV1CompaniesCompanyIdEquipmentTypeahead({
        path: { 'company-id': companyId },
        query: params,
      });

      const equipments = response.data.data.map((equipment) => {
        return EquipmentTypeahead.parse(equipment);
      });
      const paginationData = extractPagination(response);

      return { data: equipments, pagination: { after: paginationData.after } };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.equipment.failed_to_fetch'));
      throw error;
    }
  };

  const getJobEquipmentsTypeahead = async (
    companyId: string,
    jobId: string,
    query: string,
    pagination: { after?: string; limit: number },
  ) => {
    const params: GetJobEquipmentsTypeaheadQueryParams = {
      'page[limit]': pagination.limit,
      'search[query]': query || undefined,
    };

    if (pagination.after) {
      params['page[after]'] = pagination.after;
    }

    try {
      const response = await getV1CompaniesCompanyIdJobsJobIdEquipmentTypeahead({
        path: { 'company-id': companyId, 'job-id': jobId },
        query: params,
      });

      const equipments = response.data.data.map((equipment) => {
        return EquipmentTypeahead.parse(equipment);
      });
      const paginationData = extractPagination(response);

      return { data: equipments, pagination: { after: paginationData.after } };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.equipment.failed_to_fetch'));
      throw error;
    }
  };

  const checkUnfilteredResultsCancelToken = useCancelToken();
  // Date-scoped probe: keeps every non-date filter (search, states, customer/driver/vendor/etc.)
  // and drops ONLY the job start/end dates, then asks the server whether a single result exists.
  // This narrows hasUnfilteredResults to "results exist if only the dates are dropped", which is
  // the signal the auto-extend and the ClearFiltersHint both key off of (TRE-15947).
  const checkUnfilteredResults = async (companyId: string) => {
    const cancelToken = checkUnfilteredResultsCancelToken.issueToken();
    const { filters, ordersSort } = ordersDispatchStore;
    const query = buildDispatchOrdersBaseQuery(filters, ordersSort, {
      includeDates: false,
    });
    query['page[limit]'] = 1;

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });
      const hasResults = response.data.data.length > 0;
      ordersDispatchStore.setHasUnfilteredResults(hasResults);

      return hasResults;
    } catch (error) {
      if (!axios.isCancel(error)) {
        ordersDispatchStore.setHasUnfilteredResults(false);
      }

      return false;
    } finally {
      checkUnfilteredResultsCancelToken.discardToken();
    }
  };

  const outsideDateRangeProbeCancelToken = useCancelToken();
  // Out-of-range probe: keeps every non-date filter (search, states, customer/driver/vendor/etc.),
  // KEEPS the job start/end dates (includeDates: true), and asks the server — via
  // filter[job][invert_date_range]=true — whether a single result exists OUTSIDE the selected
  // range. The dates are mandatory: the BE only inverts the clause inside
  // `if (date_range = extract_inclusive_date_range filters)`, which needs BOTH bounds; omitting
  // them would make the probe count in-range rows too and raise a false-positive banner
  // (TRE-15949). Sets hasResultsOutsideDateRange from the response.
  const checkResultsOutsideDateRange = async (companyId: string) => {
    const cancelToken = outsideDateRangeProbeCancelToken.issueToken();
    const { filters, ordersSort } = ordersDispatchStore;
    // Snapshot the exact search + range this probe measures. hasResultsOutsideDateRange drives a
    // banner that is keyed off the raw flag (not derived from current filters like the auto-extend
    // banner), and clearing filters in a way that fires no new probe won't cancel this one — so a
    // probe that resolves after the dispatcher changed or cleared the search/dates must NOT write
    // its now-stale result, or it would resurrect the banner with a mismatched label (TRE-15949).
    const probedSearch = filters.search?.trim();
    const probedStartDate = filters.startDate;
    const probedEndDate = filters.endDate;
    // A probe that resolves after the dispatcher changed or cleared the search/dates must not touch
    // hasResultsOutsideDateRange — on success it would resurrect the banner with a mismatched label,
    // and on failure it would wipe a newer probe's still-valid result. The banner is keyed off the
    // raw flag (not derived from current filters like the auto-extend banner), and clearing filters
    // in a way that fires no new probe won't cancel this one, so guard BOTH write paths (TRE-15949).
    const probeStillMatchesCurrentFilters = () => {
      const current = ordersDispatchStore.filters;

      return (
        current.search?.trim() === probedSearch &&
        current.startDate === probedStartDate &&
        current.endDate === probedEndDate
      );
    };
    const query = buildDispatchOrdersBaseQuery(filters, ordersSort, {
      includeDates: true,
    });
    query['page[limit]'] = 1;
    query['filter[job][invert_date_range]'] = true;

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });

      if (probeStillMatchesCurrentFilters()) {
        ordersDispatchStore.setHasResultsOutsideDateRange(response.data.data.length > 0);
      }
    } catch (error) {
      if (!axios.isCancel(error) && probeStillMatchesCurrentFilters()) {
        ordersDispatchStore.setHasResultsOutsideDateRange(false);
      }
    } finally {
      outsideDateRangeProbeCancelToken.discardToken();
    }
  };

  const getCompanyOrdersByCompanyIdCancelToken = useCancelToken();
  const getCompanyOrdersByCompanyId = async ({
    companyId,
    forceFirstPage = false,
    suppressLoadingState = false,
  }: {
    companyId: string;
    forceFirstPage?: boolean;
    suppressLoadingState?: boolean;
  }) => {
    const { pagination, filters, ordersSort } = ordersDispatchStore;

    const query = buildDispatchOrdersBaseQuery(filters, ordersSort, {
      includeDates: true,
    });
    query['page[limit]'] = pagination.limit;

    if (!forceFirstPage) {
      if (pagination.before) {
        query['page[before]'] = pagination.before;
      } else if (pagination.after) {
        query['page[after]'] = pagination.after;
      }
    }

    if (!suppressLoadingState) {
      ordersDispatchStore.ordersFetchStart();
    }
    // A load-more (pagination) fetch continues the SAME search, so it must not cancel the
    // in-flight probes started by the fresh fetch — only a fresh (first-page) fetch supersedes
    // them. Without this guard, an infinite-scroll page-2 fetch aborts the out-of-range probe
    // before it resolves, so the tenet-1 banner never shows for full-page searches (TRE-15949).
    const isLoadMoreFetch =
      !forceFirstPage && Boolean(pagination.before || pagination.after);
    if (!isLoadMoreFetch) {
      // Cancel any in-flight unfiltered-results check from a prior search so it
      // cannot overwrite hasUnfilteredResults after this fetch resets the state.
      checkUnfilteredResultsCancelToken.issueToken();
      checkUnfilteredResultsCancelToken.discardToken();
      // Same guard for the stale out-of-range probe so it cannot overwrite
      // hasResultsOutsideDateRange after this fetch (TRE-15949).
      outsideDateRangeProbeCancelToken.issueToken();
      outsideDateRangeProbeCancelToken.discardToken();
    }

    const cancelToken = getCompanyOrdersByCompanyIdCancelToken.issueToken();

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatch({
        path: { 'company-id': companyId },
        query,
        cancelToken,
      });
      const orders = response.data.data.map((order) => Order.parse(order));
      const pagination = extractPagination(response);
      ordersDispatchStore.ordersFetchEnd(orders, pagination, {
        replaceExisting: forceFirstPage,
      });

      // Gate the auto-extend / out-of-range probes on whether THIS request was a load-more
      // (the request carried a page cursor — isLoadMoreFetch, computed above), NOT on whether
      // the RESPONSE has a next-page cursor. A full first page of results always carries a next
      // cursor, so keying off the response wrongly treated every full-page search as pagination
      // and skipped the tenet-1 probe entirely (TRE-15949).
      const searchTerm = filters?.search?.trim();
      // Tenet 1 fires only on a fresh page that HAS in-range results, under a search, with BOTH
      // date bounds set. Both bounds are required (not either): the probe inverts the date clause
      // and the BE only inverts when it can build a full inclusive range (needs start AND end) —
      // with a single bound (e.g. a half-committed range-picker selection) the inversion is skipped
      // and the probe counts in-range rows, raising a false-positive banner (TRE-15949).
      const shouldRunOutOfRangeProbe =
        !isLoadMoreFetch &&
        orders.length > 0 &&
        Boolean(searchTerm?.length) &&
        Boolean(filters.startDate) &&
        Boolean(filters.endDate);

      if (
        !isLoadMoreFetch &&
        orders.length === 0 &&
        searchTerm?.length &&
        ordersDispatchStore.hasActiveNonSearchFilters
      ) {
        // Mark the search scope unresolved for the WHOLE probe-and-decide window — not just the
        // probe request. hasUnfilteredResults flips to true mid-probe, so if the flag cleared the
        // instant the probe returned, the await boundary before the auto-extend bridge below would
        // expose one frame of !isResolvingSearchScope && hasUnfilteredResults and flash the manual
        // "other dates" empty state. Holding it until the bridge (or a no-extend outcome) keeps the
        // UI on the loading path the entire time (TRE-15947).
        ordersDispatchStore.setIsCheckingOtherDateResults(true);

        try {
          const probeHasResults = await checkUnfilteredResults(companyId);
          const hasDateRangeSet = Boolean(filters.startDate || filters.endDate);
          const dispatcherHasNotChosenScopeForTerm =
            ordersDispatchStore.userChosenDateScopeTerm !== searchTerm;

          // Tenet 2: the search matches nothing in the selected range but there ARE matches on
          // other dates — drop the dates and surface the DateScopeBanner so the dispatcher can
          // restore the range. Post-extend the dates are undefined so it cannot re-fire
          // (TRE-15947).
          if (probeHasResults && hasDateRangeSet && dispatcherHasNotChosenScopeForTerm) {
            // Order matters: clear the dates FIRST — that setFilters -> resetOrders
            // must NOT wipe autoExtendedFromRange — THEN record the prior range so the banner
            // survives the refetch the cleared dates trigger. doNotPersist keeps an auto-extended
            // scope out of localStorage; only a dispatcher-chosen scope is a saved preference.
            ordersDispatchStore.setFilters(
              { startDate: undefined, endDate: undefined },
              true,
              true,
            );
            // Key the record by the committed term so OrdersView can DERIVE banner visibility
            // (term still matches the current search + all-dates selected) — TRE-15949.
            ordersDispatchStore.setAutoExtendedFromRange({
              term: searchTerm,
              startDate: filters.startDate,
              endDate: filters.endDate,
            });
            // Clearing the dates above reset isLoadingOrders to false, but the widened refetch it
            // triggers has not started yet. Bridge that gap by re-entering the loading state now so
            // the empty states stay suppressed instead of flashing "no results" for one frame; the
            // refetch's own ordersFetchEnd clears it (TRE-15947).
            ordersDispatchStore.ordersFetchStart();
            AnalyticsController.track('dispatch_date_scope_extended');
          }
        } finally {
          // By now either the bridge has re-entered isLoadingOrders (auto-extend path) or the probe
          // result is authoritative (no-extend path), so clearing the probe flag never opens a gap.
          ordersDispatchStore.setIsCheckingOtherDateResults(false);
        }
      } else if (shouldRunOutOfRangeProbe) {
        // Tenet 1: the search DID match inside the selected range — probe whether it also matches
        // OUTSIDE it, so the DateScopeBanner can offer "show all dates". Mutually exclusive with the
        // auto-extend path above (that fires only when the in-range fetch is empty).
        await checkResultsOutsideDateRange(companyId);
      }

      // hasResultsOutsideDateRange is a persisted flag cleared only by resetOrders — which silent
      // refresh (tab focus / self-heal) skips. So on any fresh fetch that is NOT a tenet-1 probe
      // case (empty in-range, no search, or a partial range) a prior `true` would linger over an
      // empty list or beside the other-dates empty state. Clear a stale `true` here so the
      // scoped-to-range banner always reflects the current fetch; the probe path owns the flag and
      // is excluded (TRE-15949).
      if (
        !isLoadMoreFetch &&
        !shouldRunOutOfRangeProbe &&
        ordersDispatchStore.hasResultsOutsideDateRange
      ) {
        ordersDispatchStore.setHasResultsOutsideDateRange(false);
      }
    } catch (error) {
      if (!axios.isCancel(error)) {
        if (!suppressLoadingState) {
          ordersDispatchStore.ordersFetchEnd([]);
        }
        connection.handleRequestError(error, t('error_messages.orders.failed_to_fetch'));
      }
    } finally {
      getCompanyOrdersByCompanyIdCancelToken.discardToken();
    }
  };

  const getOrderDispatchNumbersByCompanyTypeahead = async (
    options: GetDispatchNumbersByCompanyTypeaheadProps,
  ) => {
    const limit = options.limit ?? 30;
    const query: GetV1CompaniesCompanyIdOrdersDispatchNumbersTypeaheadData['query'] = {
      'page[limit]': limit,
    };

    if (options?.query) {
      query['search[query]'] = options.query;
    }

    if (options?.cursor) {
      query['page[after]'] = options.cursor;
    }

    try {
      const response = await getV1CompaniesCompanyIdOrdersDispatchNumbersTypeahead({
        path: { 'company-id': options.companyId },
        query,
      });
      const dispatchNumbers = response.data.data.map((item) => ({
        dispatchNumber: item.name,
        id: item.id,
      }));
      const pagination = extractPagination(response);

      return { data: dispatchNumbers, pagination };
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_fetch'));
      throw error;
    }
  };

  const getJobsByOrder = async ({
    orderId,
    companyId,
    clearExistingJobs = true,
    signal,
  }: {
    orderId: string;
    companyId: string;
    clearExistingJobs?: boolean;
    signal?: AbortSignal;
  }) => {
    if (signal?.aborted) return;

    const emptyJobs = ordersDispatchStore.orderJobsFetchStart(orderId, clearExistingJobs);

    // Cleared synchronously so a remount in the same tick sees the order as not loading.
    const handleAbort = () =>
      ordersDispatchStore.orderJobsFetchAborted(orderId, emptyJobs);
    signal?.addEventListener('abort', handleAbort);

    // `getJobsByOrder` runs on both the Dispatch and Assigned pages, which keep separate
    // (separately persisted) filters, so the builder reads the calling page's store.
    const filterQuery: OrderJobsQuery = toOrderJobsQueryParams(
      buildOrderJobsFilter({
        assignedFilters: ordersAssignedStore.filters,
        dispatchFilters: ordersDispatchStore.filters,
        pathname: location.pathname,
      }),
    );

    try {
      const jobs = await fetchAllApiPages(
        (query, pageSignal) =>
          getV1CompaniesCompanyIdOrdersOrderIdJobs({
            path: { 'company-id': companyId, 'order-id': orderId },
            query: { ...filterQuery, ...query },
            signal: pageSignal,
          }),
        Job.parse,
        { signal },
      );

      ordersDispatchStore.orderJobsFetchEnd(orderId, jobs);
    } catch (error) {
      if (signal?.aborted) throw error;

      if (clearExistingJobs) {
        ordersDispatchStore.orderJobsFetchEnd(orderId, []);
      }
      ordersDispatchStore.orderJobsFetchFailed(orderId);
      connection.handleRequestError(
        error,
        t('error_messages.orders.failed_to_fetch_jobs_by_order'),
      );
    } finally {
      signal?.removeEventListener('abort', handleAbort);
    }
  };

  const rejectJob = async (jobId: string, companyId: string) => {
    ordersDispatchStore.orderJobRejectStart(jobId);

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdReject({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: {},
      });

      const rejectedJob = Job.parse(response.data.data);
      ordersDispatchStore.orderJobRejectEnd(jobId, rejectedJob);

      return rejectedJob;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_reject'));
      throw error;
    } finally {
      ordersDispatchStore.orderJobRejectEnd(jobId);
    }
  };

  const rejectOrder = async (orderId: string, rejectReason?: string) => {
    ordersDispatchStore.orderRejectStart(orderId);

    const body: PutV1CompaniesCompanyIdOrdersOrderIdRejectData['body'] = {};
    if (rejectReason) {
      body.reject_reason = rejectReason;
    }

    try {
      const response = await putV1CompaniesCompanyIdOrdersOrderIdReject({
        path: { 'company-id': companyId, 'order-id': orderId },
        body,
      });

      const rejectedOrder = Order.parse(response.data.data);
      ordersDispatchStore.orderRejectEnd(rejectedOrder);

      return rejectedOrder;
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.orders.failed_to_reject'));
      throw error;
    } finally {
      ordersDispatchStore.orderRejectEnd();
    }
  };

  const sendJob = async (jobId: string, companyId: string) => {
    ordersDispatchStore.orderJobSendStart(jobId);

    try {
      const response = await putV1CompaniesCompanyIdJobsJobIdRequest({
        path: { 'company-id': companyId, 'job-id': jobId },
        body: {},
      });

      const sentJob = Job.parse(response.data.data);
      ordersDispatchStore.orderJobSendEnd(jobId, sentJob);

      return sentJob;
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_do_event', { event: JobStateEvent.REQUEST }),
      );
      throw error;
    } finally {
      ordersDispatchStore.orderJobSendEnd(jobId);
    }
  };

  const subscribeToBulkAssignJobsRTU = (
    companyId: string,
    onUpdateReceived: (data: UserBulkAssignJobsChannel_Read) => void,
  ) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.UserBulkAssignJobsChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: onUpdateReceived,
        },
      );
    }

    return subscription;
  };

  const subscribeToBulkCopyAssignmentsRTU = (
    companyId: string,
    onUpdateReceived: (data: UserBulkCopyAssignmentsChannel_Read) => void,
  ) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.UserBulkCopyAssignmentsChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: onUpdateReceived,
        },
      );
    }

    return subscription;
  };

  const subscribeToJobsRTU = (companyId: string) => {
    const handleJobUpdate = ({ data }: { data: Job_Read }) => {
      const job = Job.parse(data);
      const updateReceivedIsForDeepLinkedOrder =
        ordersDispatchStore.deepLinkedOrder?.id &&
        ordersDispatchStore.deepLinkedOrder?.id === job.order?.id;

      if (updateReceivedIsForDeepLinkedOrder) {
        ordersDispatchStore.setDeepLinkedOrder();
      }

      ordersDispatchStore.upsertJob(job);
    };

    subscribeWithMultipleHandlers(
      realTimeChannels.CompanyJobUpdateChannel,
      companyId,
      ORDERS_DISPATCH_JOBS_RTU_HANDLER_KEY,
      handleJobUpdate,
    );

    return {
      unsubscribe: () =>
        unsubscribeHandler(
          realTimeChannels.CompanyJobUpdateChannel,
          companyId,
          ORDERS_DISPATCH_JOBS_RTU_HANDLER_KEY,
        ),
    };
  };

  const subscribeToOrdersRTU = (companyId: string) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.CompanyOrderUpdateChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: Order_Read }) => {
            const order = Order.parse(data);

            ordersDispatchStore.orderUpdateEnd(order);
            ordersAssignedStore.orderUpdateEnd(order);
          },
        },
      );
    }

    return subscription;
  };

  const subscribeToJobUnassignmentRTU = (companyId: string) => {
    const cable = connection.realTimeConnection;
    const channel = realTimeChannels.InaccessibleJobChannel;

    let subscription = cable?.subscriptions.subscriptions.find((sub) => {
      return sub.identifier.includes(channel);
    });

    if (!subscription) {
      subscription = cable?.subscriptions?.create?.(
        { channel, company_id: companyId },
        {
          connected() {},
          disconnected() {},
          received: ({ data }: { data: { job_id: string } }) => {
            ordersDispatchStore.removeJob(data.job_id);
          },
        },
      );
    }

    return subscription;
  };

  const updateJobEquipment = async (
    jobId: string,
    equipmentId?: string | null,
    additionalEquipmentIds?: string[],
    requestedEquipmentTypeName?: string | null,
    requestedEquipmentTypeAccountingId?: string | null,
  ) => {
    const isUpdatingEquipment = !isUndefined(equipmentId);
    const isUpdatingAdditionalEquipment = !isUndefined(additionalEquipmentIds);
    const isUpdatingRequestedEquipmentTypeName = !isUndefined(requestedEquipmentTypeName);
    const isUpdatingRequestedEquipmentTypeAccountingId = !isUndefined(
      requestedEquipmentTypeAccountingId,
    );

    if (
      !isUpdatingEquipment &&
      !isUpdatingAdditionalEquipment &&
      !isUpdatingRequestedEquipmentTypeName &&
      !isUpdatingRequestedEquipmentTypeAccountingId
    ) {
      throw new Error(
        'Please define either equipment, additional equipment, requested equipment type name or requested equipment type accounting id.',
      );
    }

    ordersDispatchStore.orderJobUpdateStart(jobId);

    const payload: Job_Update = {};

    if (isUpdatingEquipment) {
      payload.equipment_id = equipmentId;
    }

    if (isUpdatingAdditionalEquipment) {
      payload.additional_equipment_ids = additionalEquipmentIds;
    }

    if (isUpdatingRequestedEquipmentTypeName) {
      payload.requested_equipment_type_name = requestedEquipmentTypeName;
    }

    if (isUpdatingRequestedEquipmentTypeAccountingId) {
      payload.requested_equipment_type_accounting_id = requestedEquipmentTypeAccountingId;
    }

    try {
      const response = await patchV1CompaniesCompanyIdJobsId({
        path: { 'company-id': companyId, id: jobId },
        body: payload,
      });
      const updatedJob = Job.parse(response.data.data);
      ordersDispatchStore.orderJobUpdateEnd(jobId, updatedJob);

      return updatedJob;
    } catch (error) {
      ordersDispatchStore.orderJobUpdateEnd(jobId);
      connection.handleRequestError(error, t('error_messages.jobs.failed_to_update'));
      throw error;
    }
  };

  const updateOrder = async (order: NewOrderFormSchemaInterface) => {
    ordersDispatchStore.orderUpdateStart();

    try {
      if (!order.id) {
        throw new Error('Order id is required');
      }

      const distanceUnit = shouldUseKilometers
        ? UnitOfDistance.KILOMETER
        : UnitOfDistance.MILE;

      const response = await patchV1CompaniesCompanyIdOrdersId({
        path: { 'company-id': companyId, id: order.id },
        body: Order.deparseUpdate(order, distanceUnit),
      });

      const updatedOrder = Order.parse(response.data.data);
      ordersDispatchStore.orderUpdateEnd(updatedOrder);

      return updatedOrder;
    } catch (error) {
      ordersDispatchStore.orderUpdateEnd();
      connection.handleRequestError(error, t('error_messages.orders.failed_to_update'));
      throw error;
    }
  };

  // order-form-v3: sparse-PATCH twin of updateOrder —
  // see the createOrderV3 docblock above for the V3 body/422 contract. The
  // body is deparseUpdateV3's dirtyFields-gated output; OrderForm.tsx skips
  // the call entirely when that body is empty.
  const updateOrderV3 = async ({
    id,
    body,
  }: {
    id: string;
    body: Partial<Order_Update>;
  }) => {
    ordersDispatchStore.orderUpdateStart();

    try {
      if (!id) {
        throw new Error('Order id is required');
      }

      const response = await patchV1CompaniesCompanyIdOrdersId({
        path: { 'company-id': companyId, id },
        body,
      });

      const updatedOrder = Order.parse(response.data.data);
      ordersDispatchStore.orderUpdateEnd(updatedOrder);

      return updatedOrder;
    } catch (error) {
      ordersDispatchStore.orderUpdateEnd();

      if (isAxiosError(error) && error.response?.status === 422) {
        throw error;
      }

      connection.handleRequestError(error, t('error_messages.orders.failed_to_update'));
      throw error;
    }
  };

  const handleAssigneeDragStart = (event: DragStartEvent) => {
    const { assigneeId, equipmentTypeId } = parseAssigneeDragId(String(event.active.id));
    const assignee = ordersDispatchStore.driverPanelAssignees.find((assignee) => {
      return assignee.id === assigneeId;
    });

    ordersDispatchStore.setDraggedAssignee(assignee, equipmentTypeId);
  };

  const executeDragAssignment = async (
    jobId: string,
    assigneeId: string,
    orderId: string,
    isDriver: boolean,
    complianceOverride?: boolean,
    equipmentTypeId?: string,
  ) => {
    const orderJob = (ordersDispatchStore.orderJobs.get(orderId) ?? []).find(
      (job) => job.id === jobId,
    );

    if (!orderJob) {
      return;
    }

    const isAssignedToDriver = orderJob.driver;
    const isAssignedToVendor = orderJob.vendorJobAssignment;

    ordersDispatchStore.orderJobUpdateStart(jobId);

    try {
      if (isAssignedToDriver) {
        await unassignDriver(jobId, companyId);
      } else if (isAssignedToVendor) {
        await doEvent(jobId, JobStateEvent.UNASSIGN, companyId);
      }

      let updatedJob: Job | undefined;

      if (isDriver) {
        updatedJob = await assignDriver(jobId, assigneeId, companyId, complianceOverride);
      } else {
        updatedJob = await assignVendor(
          jobId,
          assigneeId,
          companyId,
          complianceOverride,
          equipmentTypeId,
        );
      }

      ordersDispatchStore.orderJobUpdateEnd(jobId, updatedJob);
      AnalyticsController.track('job_assignment_submitted', {
        method: 'drag_drop',
        assignee_type: isDriver ? 'driver' : 'vendor',
        job_count: 1,
      });
      toasterStore.push(
        alert(t('dispatch.job.job_assigned_successfully'), AlertTypes.success),
      );
    } catch {
      ordersDispatchStore.orderJobUpdateEnd(jobId);
    }
  };

  const checkComplianceThenAssign = async (
    jobId: string,
    assigneeId: string,
    orderId: string,
    isDriver: boolean,
    equipmentTypeId?: string,
  ) => {
    const checkResult = await checkCompliance(
      companyId,
      isDriver ? undefined : assigneeId,
      isDriver ? assigneeId : undefined,
    );

    if (checkResult.hasIssues) {
      setPendingDragAssignment({
        jobId,
        assigneeId,
        orderId,
        isDriver,
        equipmentTypeId,
        complianceResult: checkResult,
      });
      return;
    }

    await executeDragAssignment(
      jobId,
      assigneeId,
      orderId,
      isDriver,
      undefined,
      equipmentTypeId,
    );
  };

  const handleAssigneeDragEnd = async (event: DragEndEvent) => {
    try {
      const { draggedAssignee, draggedAssigneeEquipmentTypeId } = ordersDispatchStore;

      if (draggedAssignee && event.over?.id) {
        const assigneeId = draggedAssignee.id;
        const { jobId, orderId } = JSON.parse(String(event.over.id));
        const isDriver = draggedAssignee instanceof DriverTypeahead;
        const vendor = isDriver ? null : draggedAssignee;
        const requestedEquipmentType = resolveRequestedEquipmentType(
          (ordersDispatchStore.orderJobs.get(orderId) ?? []).find(
            (job) => job.id === jobId,
          ),
          ordersDispatchStore.orders.find((order) => order.id === orderId),
        );
        const equipmentTypeName =
          ordersDispatchStore.draggedAssigneeTruckCapacity?.equipmentType.name;

        if (
          vendor &&
          draggedAssigneeEquipmentTypeId &&
          isRequestedEquipmentTypeMismatch(
            requestedEquipmentType,
            draggedAssigneeEquipmentTypeId,
            equipmentTypeName,
          )
        ) {
          setPendingTypeMismatchAssignment({
            jobId,
            assigneeId,
            orderId,
            equipmentTypeId: draggedAssigneeEquipmentTypeId,
            equipmentTypeName: equipmentTypeName ?? t('form_fields.equipment_type'),
            requestedEquipmentTypeName:
              requestedEquipmentType?.name ?? t('form_fields.equipment_type'),
            vendorName: vendor.name,
          });
          return;
        }

        await checkComplianceThenAssign(
          jobId,
          assigneeId,
          orderId,
          isDriver,
          draggedAssigneeEquipmentTypeId,
        );
      }
    } catch {
      // Error is handled in nested operations.
    } finally {
      ordersDispatchStore.setDraggedAssignee();
    }
  };

  const confirmTypeMismatchAssignment = async () => {
    if (!pendingTypeMismatchAssignment) {
      return;
    }

    const { jobId, assigneeId, orderId, equipmentTypeId } = pendingTypeMismatchAssignment;
    setIsTypeMismatchSubmitting(true);

    try {
      await checkComplianceThenAssign(jobId, assigneeId, orderId, false, equipmentTypeId);
    } catch {
      // Error is handled in nested operations.
    } finally {
      setIsTypeMismatchSubmitting(false);
      setPendingTypeMismatchAssignment(null);
    }
  };

  const cancelTypeMismatchAssignment = () => {
    setPendingTypeMismatchAssignment(null);
  };

  const confirmDragAssignment = async () => {
    if (pendingDragAssignment) {
      const { jobId, assigneeId, orderId, isDriver, equipmentTypeId } =
        pendingDragAssignment;
      setIsDragOverrideSubmitting(true);
      try {
        await executeDragAssignment(
          jobId,
          assigneeId,
          orderId,
          isDriver,
          true,
          equipmentTypeId,
        );
      } finally {
        setIsDragOverrideSubmitting(false);
        setPendingDragAssignment(null);
      }
    }
  };

  const cancelDragAssignment = () => {
    setPendingDragAssignment(null);
  };

  const handleAssigneeDragCancel = () => {
    ordersDispatchStore.setDraggedAssignee();
  };

  // Deliberately does not catch: both consumers own their error toast and busy state.
  const fetchAllJobsForExport = useCallback(() => {
    const { filters } = ordersDispatchStore;
    const filterQuery: CompanyJobsQuery = {};

    if (filters?.search?.trim().length) {
      filterQuery['search[datagrid]'] = filters.search.trim();
    }

    if (filters?.customerAccountIds?.length) {
      filterQuery['filter[customer_account_ids]'] = filters.customerAccountIds;
    }

    if (filters?.driverIds?.length) {
      filterQuery['filter[driver_ids]'] = filters.driverIds;
    }

    if (filters?.dropoffSites?.length) {
      filterQuery['filter[dropoff_site_ids]'] = filters.dropoffSites;
    }

    if (filters?.endDate?.length) {
      filterQuery['filter[end_date]'] = dayjs
        .tz(filters.endDate)
        .endOf('day')
        .toISOString();
    }

    if (filters?.projectsExternalIds?.length) {
      filterQuery['filter[external_ids]'] = filters.projectsExternalIds;
    }

    if (filters?.foremen?.length) {
      filterQuery['filter[foreman_ids]'] = filters.foremen;
    }

    if (filters?.pickUpSites?.length) {
      filterQuery['filter[pickup_site_ids]'] = filters.pickUpSites;
    }

    if (filters?.projectIds?.length) {
      filterQuery['filter[project_ids]'] = filters.projectIds;
    }

    if (filters?.siteTypes?.length) {
      filterQuery['filter[site_types]'] = filters.siteTypes;
    }

    if (filters?.startDate?.length) {
      filterQuery['filter[start_date]'] = dayjs
        .tz(filters.startDate)
        .startOf('day')
        .toISOString();
    }

    if (filters?.jobStates?.length) {
      filterQuery['filter[states]'] = filters.jobStates;
    }

    if (filters?.dispatchStatuses?.length) {
      filterQuery['filter[dispatch_statuses]'] = filters.dispatchStatuses;
    }

    if (filters?.vendorAccountIds?.length) {
      filterQuery['filter[vendor_account_ids]'] = filters.vendorAccountIds;
    }

    if (filters?.equipmentOwnerCompanyIds?.length) {
      filterQuery['filter[equipment][company_ids]'] = filters.equipmentOwnerCompanyIds;
    }

    return fetchAllApiPages(
      (query) =>
        getV1CompaniesCompanyIdJobs({
          path: { 'company-id': getEffectiveUserCompanyId(userStore) },
          query: { ...filterQuery, ...query },
        }),
      Job.parse,
    );
  }, [ordersDispatchStore, userStore]);

  return {
    acceptJob,
    acceptOrder,
    bulkAssignJobs,
    bulkCancelJobs,
    bulkCopyAssignments,
    bulkSendJobs,
    bulkSendJobsByDay,
    bulkUnassignJobs,
    cancelDragAssignment,
    cancelJob,
    cancelOrder,
    cancelTypeMismatchAssignment,
    cloneOrderV2,
    completeOrder,
    confirmDragAssignment,
    confirmTypeMismatchAssignment,
    createJobFromOrder,
    createOrder,
    createOrderV3,
    deleteJob,
    duplicateJob,
    fetchAllJobsForExport,
    getBulkSendJobsByDayCount,
    getCompanyEquipmentsTypeahead,
    getJobEquipmentsTypeahead,
    getCompanyOrdersByCompanyId,
    getJobsByOrder,
    getOrderDispatchNumbersByCompanyTypeahead,
    handleAssigneeDragCancel,
    handleAssigneeDragEnd,
    handleAssigneeDragStart,
    isDragOverrideSubmitting,
    isTypeMismatchSubmitting,
    pendingDragAssignment,
    pendingTypeMismatchAssignment,
    rejectJob,
    rejectOrder,
    sendJob,
    subscribeToBulkAssignJobsRTU,
    subscribeToBulkCopyAssignmentsRTU,
    subscribeToJobUnassignmentRTU,
    subscribeToJobsRTU,
    subscribeToOrdersRTU,
    updateJobEquipment,
    updateOrder,
    updateOrderV3,
  };
}
